<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: LoginPage                                             |
| Section: views/auth                                              |
| Purpose: Login route handling credential submission and errors.  |
+------------------------------------------------------------------+
-->
<template>
    <div class="wrapper">
        <h1>{{ $t('login.title') }}</h1>
        <div class="Login_wrapper">
            <form @submit.prevent="submitLogin">
                <div class="form-group">
                    <label for="username">{{ $t('login.username') }}</label>
                    <input type="text" v-model="username" id="username" name="username" required>
                </div>
                <div class="form-group">
                    <label for="password">{{ $t('login.password') }}</label>
                    <input type="password" v-model="password" id="password" name="password" required>
                </div>
                <div class="form-options">
                    <label class="custom-checkbox">
                        <input type="checkbox" v-model="rememberMe">
                        <span class="checkbox-icon"></span>
                        {{ $t('login.rememberMe') }}
                    </label>
                    <a :href="passwordResetMailto" class="password-reset-link">
                        {{ $t('login.forgotPassword') }}
                    </a>
                </div>
                <button type="submit" class="login-btn">
                    <img src="@/assets/icons/login.svg"> {{ $t('login.submit') }}
                </button>
            </form>

            <!-- Display success or error message -->
            <p v-if="successMessage" class="success-message">{{ successMessage }}</p>
            <p v-if="errorMessage" class="error-message">{{ errorMessage }}</p>
        </div>
        <!-- <div class="TitleBtnGroup">
            <h3>Heb je nog geen account?</h3>
            <a href="mailto:info@sejarah-thumann.online?subject=Accountaanvraag: Toegang tot de stamboom&body=Beste Jeffrey,%0D%0A%0D%0ADe stamboom bevat gevoelige familie-informatie. Om toegang te krijgen, licht ik hieronder kort toe waarom ik een account wil aanvragen.%0D%0A%0D%0AVerwantschap: [Beschrijf hier hoe je verwant bent aan de familie]%0D%0AReden voor toegang: [Beschrijf hier waarom je toegang wilt]%0D%0A%0D%0AIk begrijp dat mijn verzoek zorgvuldig wordt beoordeeld met het oog op privacy.%0D%0A%0D%0AMet vriendelijke groet,%0D%0A[Je naam]"
                class="ghost_button">
                Account aanvragen <img src="@/assets/icons/register.svg">
            </a>
        </div> -->
    </div>
</template>

<script>
import { login } from '@/api/loginService';
import { inject } from 'vue';
import { useLocale } from '@/composables/useLocale';

export default {
    name: 'LoginPage',
    setup() {
        const loginFunction = inject('login'); // Inject the provided login function from App.vue
        const router = inject('$router'); // To redirect after login if needed
        const { localePath } = useLocale();

        return {
            loginFunction,
            router,
            localePath
        };
    },
    data() {
        return {
            username: '',
            password: '',
            rememberMe: true,
            errorMessage: '',
            successMessage: '' // To display success feedback
        };
    },
    computed: {
        passwordResetMailto() {
            const subject = this.$t('login.passwordResetSubject');
            const body = this.$t('login.passwordResetBody');
            return `mailto:info@sejarah-thumann.online?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
        }
    },
    methods: {
        async submitLogin() {
            try {
                const data = await login(this.username, this.password);
                this.loginFunction(this.username, data.token, this.rememberMe);

                // Clear any existing error message and set success message
                this.errorMessage = '';
                this.successMessage = this.$t('login.successRedirect');

                // Redirect after a delay
                setTimeout(() => {
                    const redirectPath = typeof this.$route.query.redirect === 'string'
                        ? this.$route.query.redirect
                        : this.localePath('/');
                    this.$router.push(redirectPath);
                }, 2000);
            } catch (error) {
                // Clear any existing success message and set error message on failure
                this.successMessage = '';
                this.errorMessage = error.message;
            }
        }
    }
};
</script>

<style scoped>
.wrapper {
    padding: var(--page-padding);
    min-height: calc(100vh - 106px - 516px);
    display: flex;
    flex-direction: column;
    gap: 24px;
}

h1 {
    text-align: center;
}

.Login_wrapper {
    background: var(--brand-bg-1);
    box-shadow: var(--shadow-card-lg);
    padding: 48px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 32px;
    width: 100%;
    max-width: 480px;
    margin: auto;
}

form {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 24px;
    align-self: stretch;
}

.form-group {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    align-self: stretch;
}

.form-group input {
    display: flex;
    height: 56px;
    padding: 16px 24px;
    justify-content: space-between;
    align-items: center;
    align-self: stretch;
    border-radius: 4px;
    border: 1px solid #DDD;
    background: #FFF;
}

.form-options {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    align-self: stretch;
}

.form-options label {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
}

form a {
    color: var(--brand-color-1);
    font-size: 14px;
}

a:hover {
    text-decoration: underline;
}

.login-btn {
    display: flex;
    height: 51px;
    padding: 12px 24px;
    justify-content: center;
    align-items: center;
    gap: 8px;
    align-self: stretch;
    border-radius: var(--border-radius-pill);
    background: var(--brand-color-1);
    color: var(--brand-text-light);
    border: 2px solid var(--brand-color-1);
    transition: var(--transition-bounce);
    box-shadow: 0px 0px 0px 0px var(--brand-color-1);
    cursor: pointer;
    font-weight: 600;
    margin-top: 24px;
}

.login-btn:hover {
    box-shadow: 0px 0px 0px 4px var(--brand-color-1);
    text-decoration: underline;
}

.TitleBtnGroup {
    margin-top: 48px;
}

/* Hide the default checkbox */
.custom-checkbox input[type="checkbox"] {
    display: none;
}

/* The custom checkbox wrapper */
.custom-checkbox {
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;
}

/* The custom checkbox icon */
.checkbox-icon {
    width: 16px;
    height: 16px;
    background-image: url('@/assets/icons/custom-checkbox-unchecked.svg');
    background-size: contain;
    background-repeat: no-repeat;
    background-position: center;
    transition: all 0.3s ease;
}

/* Change the icon when checked */
.custom-checkbox input[type="checkbox"]:checked+.checkbox-icon {
    background-image: url('@/assets/icons/custom-checkbox-checked.svg');
}

@media (max-width: 860px) {
    .Login_wrapper {
        padding: 24px;
    }

    .wrapper {
        padding: var(--page-padding-mobile);
    }

    .form-group input {
        padding: 12px 16px;
        height: auto;
    }

    .form-options {
        flex-direction: column;
        gap: 8px;
    }

    .login-btn {
        margin-top: 0;
    }
}
</style>
